/**
 * The `secret` correlation token, and how it rides inside WhatsApp's
 * `messageContextInfo.messageSecret`.
 *
 * messageSecret wire format (v2 — marked):
 *   [CALLER_TOKEN_MARKER (4 bytes)][token UTF-8 bytes][zero-pad to 32]
 *
 * or, READ-ONLY backward compat (secrets written before the marker existed):
 *   [token UTF-8 bytes][zero-pad to 32]
 *
 * ── WHAT THE MARKER IS, AND WHAT IT IS NOT ─────────────────────────────────
 *
 * It is a STRUCTURAL claim and nothing more: *this messageSecret is a caller's
 * correlation token that we wrote, not WhatsApp's own E2E secret.* It carries
 * no meaning about who sent the message or why.
 *
 * That distinction is the reason it exists. `messageSecret` is not our field —
 * on a message we did not send it holds WhatsApp's E2E material. Anything that
 * wants to READ a token back out (the `secret` on a message the API returns,
 * the ack matcher, a webhook payload) must first answer "is this ours?", and
 * the only stateless way to answer it is a marker we control. Without one the
 * best available test is a shape heuristic — "does it decode to something that
 * looks like an ObjectId" — which is wrong in both directions: it rejects a
 * caller's own token format, and it would hand a caller bytes of a real E2E
 * secret whenever random bytes happened to fit the shape.
 *
 * The MEANING of a token belongs to whoever wrote it. A caller that wants to
 * recognise its own outgoing messages puts its own self-identifying value in
 * `secret` (e.g. the LLM bridge tags its sends so it can spot its own echoes);
 * that is the caller's scheme and this module knows nothing about it. An
 * earlier version of this file stamped the marker as a "🤖 Blueticks bot sent
 * this" flag — that was one caller's policy leaking into the platform's wire
 * format, and it is deliberately gone.
 *
 * ── LAYOUT MATH ────────────────────────────────────────────────────────────
 * marker(4) + ObjectId(24) = 28 ≤ 32, so a normal send carries BOTH the marker
 * and the full id with 4 bytes of zero pad to spare. A token longer than 28
 * bytes leaves no room for the marker and throws — every secret we write is
 * marked (an unmarked one would be indistinguishable from a native secret).
 *
 * ── BACKWARD COMPAT ────────────────────────────────────────────────────────
 * `decodeUserMessageIdFromMessageSecret` reads BOTH formats, so messages that
 * predate the marker still recover their exact token. An old-format secret is
 * ascii-hex whose first byte is never 0xF0, so it can never be mistaken for a
 * marked one.
 *
 * Both engines encode the token `resolveWireSecret` (utils/send-prep.ts) picks
 * with this one encoder, so they write identical secrets with no version-byte
 * or decoder bridge.
 *
 * Why no salt: an `_id` token is itself unique (Mongo ObjectId carries
 * timestamp+machine+pid+counter). A caller `secret` token is not, so
 * the send-protocol store-check runs only for an `_id` token.
 */

/**
 * The 4-byte prefix that claims "we wrote this messageSecret".
 *
 * The bytes are the UTF-8 encoding of U+1F916, chosen when this was a bot flag
 * and KEPT ON PURPOSE: changing them would strand every message already sent
 * with the old prefix, and the value is arbitrary anyway — what matters is that
 * it is a fixed sequence a native E2E secret is overwhelmingly unlikely to
 * begin with, and that both engines agree on it. Treat it as an opaque magic
 * number, not as an emoji.
 */
export const CALLER_TOKEN_MARKER = new Uint8Array([0xf0, 0x9f, 0xa4, 0x96])

/**
 * True when a 32-byte secret is one WE wrote — i.e. the value decoded out of it
 * is a caller correlation token and is safe to hand back to that caller.
 *
 * False for legacy (unmarked) secrets and for WhatsApp's own random ones. A
 * random native secret matches ~1/2^32 of the time, and even then the bytes
 * after it are not a token anyone issued.
 */
export function messageSecretIsCallerToken(secret: Uint8Array | undefined | null): boolean {
    if (!secret || secret.length !== 32) return false
    return startsWithMarker(secret)
}

/**
 * The caller's token, or null when this secret is not ours.
 *
 * THE READ-SIDE ENTRY POINT: anything that surfaces `secret` outward — the v1
 * message projection, a webhook payload, the engine's message tools — must go
 * through this rather than decoding directly, because a decode alone cannot
 * tell a token from E2E material and would leak the latter.
 */
export function decodeCallerTokenFromMessageSecret(secret: Uint8Array | undefined | null): string | null {
    if (!messageSecretIsCallerToken(secret)) return null
    return decodeZeroTerminatedUtf8(secret as Uint8Array, CALLER_TOKEN_MARKER.length)
}

/** Largest token (UTF-8 bytes) that fits beside the marker in the 32-byte secret: 28. */
export const MAX_CALLER_TOKEN_BYTES = 32 - CALLER_TOKEN_MARKER.length

/** True when `token` can be carried as a marked secret (1..28 UTF-8 bytes). */
export function callerTokenFits(token: string): boolean {
    const len = new TextEncoder().encode(token).length
    return len > 0 && len <= MAX_CALLER_TOKEN_BYTES
}

/** `[marker][token][zero-pad]`; throws when the token does not fit beside the marker (> 28 bytes). */
export function encodeUserMessageIdAsMessageSecret(userMessageId: string): Uint8Array {
    const idBytes = new TextEncoder().encode(userMessageId)
    if (idBytes.length > MAX_CALLER_TOKEN_BYTES) {
        throw new Error(`userMessageId too long: ${idBytes.length} bytes (max ${MAX_CALLER_TOKEN_BYTES})`)
    }
    const out = new Uint8Array(32)
    out.set(CALLER_TOKEN_MARKER, 0)
    out.set(idBytes, CALLER_TOKEN_MARKER.length)
    // remaining bytes stay zero (intentional terminator + pad)
    return out
}

/**
 * The token embedded in a secret, reading BOTH formats.
 *
 * Deliberately permissive, and therefore NOT the function to use when
 * surfacing a value outward: on an unmarked secret it returns whatever the
 * bytes decode to, which for a native message is E2E material. Use
 * {@link decodeCallerTokenFromMessageSecret} for anything caller-facing; this
 * one is for internal matching against a value we already know we sent.
 */
export function decodeUserMessageIdFromMessageSecret(secret: Uint8Array | undefined | null): string | null {
    if (!secret || secret.length !== 32) return null
    const start = startsWithMarker(secret) ? CALLER_TOKEN_MARKER.length : 0
    return decodeZeroTerminatedUtf8(secret, start)
}

/** Prefix check independent of total length — used for stripping on decode. */
function startsWithMarker(buf: Uint8Array): boolean {
    if (buf.length < CALLER_TOKEN_MARKER.length) return false
    for (let i = 0; i < CALLER_TOKEN_MARKER.length; i++) {
        if (buf[i] !== CALLER_TOKEN_MARKER[i]) return false
    }
    return true
}

function decodeZeroTerminatedUtf8(buf: Uint8Array, start: number): string | null {
    let end = start
    while (end < buf.length && buf[end] !== 0) end++
    if (end === start) return null
    return new TextDecoder().decode(buf.subarray(start, end))
}
